import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { UserRound } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { Appearance } from './Appearance'
import RoleBadge from '@/components/auth/RoleBadge'
import SignOutButton from '@/components/auth/SignOutButton'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { hasOverlayAbove, OVERLAY_LAYER_ATTR } from '@/utils/overlayLayers'

interface Props {
  navigationOpen: boolean
  isDesktop: boolean
}

/** Account disclosure kept inside the navigation's DOM and mobile focus boundary. */
export function IslandAccount({ navigationOpen, isDesktop }: Props) {
  const { accounts } = useMsal()
  const authenticated = useIsAuthenticated()
  const { user, role, isLoading } = useCurrentUser()
  const { pathname } = useLocation()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ left: 0, top: 0, width: 240 })
  const displayName =
    user?.display_name?.trim() ||
    accounts[0]?.name?.trim() ||
    accounts[0]?.username?.trim() ||
    user?.email?.trim() ||
    'Your account'
  const initial =
    displayName === 'Your account'
      ? null
      : Array.from(displayName)[0].toLocaleUpperCase()

  const close = useCallback((restoreFocus = true) => {
    // Don't steal focus from a console panel arriving above the navigation.
    if (
      restoreFocus &&
      panelRef.current?.contains(document.activeElement) &&
      !hasOverlayAbove('float')
    ) {
      triggerRef.current?.focus()
    }
    setOpen(false)
  }, [])

  useEffect(() => {
    close()
  }, [pathname, navigationOpen, isDesktop, close])

  useLayoutEffect(() => {
    if (!open) return
    const panel = panelRef.current
    const trigger = triggerRef.current
    const sidebar = rootRef.current?.closest('aside')
    if (!panel || !trigger || !sidebar) return

    const place = () => {
      const viewport = window.visualViewport
      const edge = 12
      const viewportLeft = viewport?.offsetLeft ?? 0
      const viewportTop = viewport?.offsetTop ?? 0
      const viewportWidth = viewport?.width ?? window.innerWidth
      const viewportHeight = viewport?.height ?? window.innerHeight
      const right = viewportLeft + viewportWidth - edge
      const bottom = viewportTop + viewportHeight - edge
      const anchor = trigger.getBoundingClientRect()
      const rail = sidebar.getBoundingClientRect()
      const beside = right - rail.right - edge >= 200
      const width = Math.min(
        240,
        beside ? right - rail.right - edge : viewportWidth - edge * 2
      )
      // A fixed descendant escapes the rail's overflow clipping while remaining
      // in DOM order, so Tab and the mobile trap include the sign-out button.
      panel.style.maxHeight = `${viewportHeight - edge * 2}px`
      const height = panel.getBoundingClientRect().height
      const nextPosition = {
        width,
        left: Math.max(
          viewportLeft + edge,
          Math.min(beside ? rail.right + edge : rail.left + edge, right - width)
        ),
        top: Math.max(
          viewportTop + edge,
          Math.min(
            beside ? anchor.bottom - height : anchor.top - height - edge,
            bottom - height
          )
        ),
      }
      setPosition((previous) =>
        previous.left === nextPosition.left &&
        previous.top === nextPosition.top &&
        previous.width === nextPosition.width
          ? previous
          : nextPosition
      )
    }
    place()
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(place)
    })
    observer.observe(panel)
    observer.observe(sidebar)
    window.addEventListener('resize', place)
    window.visualViewport?.addEventListener('resize', place)
    window.visualViewport?.addEventListener('scroll', place)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener('resize', place)
      window.visualViewport?.removeEventListener('resize', place)
      window.visualViewport?.removeEventListener('scroll', place)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const outside = (event: Event) => {
      if (
        event.target instanceof Node &&
        !rootRef.current?.contains(event.target)
      )
        close()
    }
    const escape = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        hasOverlayAbove('float')
      )
        return
      event.preventDefault()
      close()
    }
    const observer = new MutationObserver(() => {
      if (hasOverlayAbove('float')) close(false)
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [OVERLAY_LAYER_ATTR],
    })
    document.addEventListener('pointerdown', outside)
    document.addEventListener('focusin', outside)
    // Consume the first Escape before the navigation's own dismissal listener.
    document.addEventListener('keydown', escape, true)
    return () => {
      observer.disconnect()
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('focusin', outside)
      document.removeEventListener('keydown', escape, true)
    }
  }, [open, close])

  if (!authenticated) return null

  return (
    <div ref={rootRef} className="island-account">
      <button
        ref={triggerRef}
        type="button"
        className="island-nav-row island-account-trigger"
        aria-label={`Account: ${displayName}`}
        aria-expanded={open}
        aria-controls={panelId}
        title="Your account"
        onClick={() => {
          if (open) close()
          else if (!hasOverlayAbove('float')) setOpen(true)
        }}
      >
        <span className="island-nav-icon" aria-hidden="true">
          <span className="island-account-avatar">
            {initial ?? <UserRound className="h-5 w-5" />}
          </span>
        </span>
        <span className="island-nav-label">Account</span>
      </button>
      {/* Keep the sign-out lifecycle mounted when the disclosure closes. */}
      <div
        ref={panelRef}
        id={panelId}
        role="region"
        aria-label="Your account"
        className="island-account-panel"
        style={position}
        hidden={!open}
      >
        <p className="island-account-name">{displayName}</p>
        <div className="island-account-role">
          {isLoading ? (
            <span>Checking access…</span>
          ) : (
            <RoleBadge role={role} appearance="muted" />
          )}
        </div>
        <Appearance />
        <div className="island-account-actions">
          <SignOutButton className="island-account-signout" />
        </div>
      </div>
    </div>
  )
}
